import { useCallback, useEffect, useMemo, useRef } from 'react'

export const useModal = ({
	isOpen,
	onClose,
	closeOnBackdrop = true,
	closeOnEsc = true,
	initialFocusRef,
	width = 568,
	maxHeight = 648,
}) => {
	const modalRef = useRef(null)

	const onBackdropMouseDown = useCallback(() => {
		if (!closeOnBackdrop) return
		onClose?.()
	}, [closeOnBackdrop, onClose])

	useEffect(() => {
		if (!isOpen || !closeOnEsc) return

		const onKeyDown = e => {
			if (e.key === 'Escape') onClose?.()
		}

		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [isOpen, closeOnEsc, onClose])

	useEffect(() => {
		if (!isOpen) return

		const t = setTimeout(() => {
			if (initialFocusRef?.current) {
				initialFocusRef.current.focus?.()
				return
			}

			const el =
				modalRef.current?.querySelector(
					'input, textarea, select, button, [tabindex]:not([tabindex="-1"])'
				) || null

			el?.focus?.()
		}, 0)

		return () => clearTimeout(t)
	}, [isOpen, initialFocusRef])

	useEffect(() => {
		if (!isOpen) return
		const prev = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = prev
		}
	}, [isOpen])

	const sizeStyle = useMemo(
		() => ({
			width: `min(${width}px, calc(100vw - 32px))`,
			maxHeight: `min(${maxHeight}px, calc(100vh - 32px))`,
		}),
		[width, maxHeight]
	)

	return {
		modalRef,
		onBackdropMouseDown,
		sizeStyle,
	}
}
